import { ReactNode, useEffect, useMemo } from 'react';
import {
    CellContext,
    ColumnDef,
    createColumnHelper,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    HeaderContext,
    OnChangeFn,
    Row,
    RowSelectionState,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import classNames from 'classnames';
import fuzzyFilter from '@components/primitives/dataGrid/utils/filters/fuzzyFilter';
import numericFilter from '@components/primitives/dataGrid/utils/filters/numericFilter';
import useDataGridState from '@components/primitives/dataGrid/hooks/useDataGridState';
import numericSorting from '@components/primitives/dataGrid/utils/sorting/numericSorting';
import IndeterminateCheckbox from '@components/primitives/dataGrid/components/cells/IndeterminateCheckbox';
import { purifyColumnFilters, purifyColumnOrder, purifySorting } from '@components/primitives/dataGrid/utils/purifyDataGridState';
import { DocListState } from '@hooks/useDocListInfo';
import { DynamicField, DynamicFieldFeedback } from '@models/DynamicField';
import DataGridFooter, { DataGridFooterOptionalProps } from '@components/primitives/dataGrid/DataGridFooter';
import DataGridHead from './DataGridHead';
import DataGridBody from '@components/primitives/dataGrid/DataGridBody';
import dateRangeFilter from '@components/primitives/dataGrid/utils/filters/dateRangeFilter';
import selectFilter from '@components/primitives/dataGrid/utils/filters/selectFilter';
import { NewNumericFilterValue, NumericFilterValue } from '@components/primitives/dataGrid/components/filters/NumericFilter';
import { SelectFilterValue } from '@components/primitives/dataGrid/components/filters/SelectFilter';
import { useHotkeys } from 'react-hotkeys-hook';
import { ARROW_DOWN, ARROW_UP } from '@constants/hotkeys';
import { ModalType } from '@models/modals/Modals';
import { Sizes } from '@utils/sizes';
import useModals from '@components/modals/hooks/useModals';
import { ColumnSetting } from '@components/modals/gridSettingsModal/GridSettingsModal';
import { useTranslation } from 'react-i18next';

export type ColumnFilterValue = NumericFilterValue | NewNumericFilterValue | SelectFilterValue | string;

export interface ColumnFilter {
    id: string;
    value: ColumnFilterValue;
}

export type ColumnFiltersState = Array<ColumnFilter>;

export interface Field {
    id: string;
    display?: string;
    feedback?: DynamicFieldFeedback;
}

interface Props<TData> extends DataGridFooterOptionalProps {
    // eslint-disable-next-line
    columns: ColumnDef<TData, any>[];
    data: TData[];
    name: string;
    className?: string;
    pageSize?: number;
    getRowProps?: (row: Row<TData>) => object;
    storageKey?: string;
    disableSettings?: boolean;
    selectedRow?: string;
    selectedCell?: keyof TData;
    onCellSelect?: (rowId: string, columnId: keyof TData) => void;
    onOrderChange?: (state: DocListState) => void;
    rowSelection?: RowSelectionState;
    setRowSelection?: OnChangeFn<RowSelectionState>;
    enableRowSelection?: (row: Row<TData>) => boolean;
    renderNrOfRows?: (nrOfRows: number, nrOfFilteredRows: number, selectedIndex: number) => ReactNode;
    onFieldUpdated?: (rowId: string, columnId: keyof TData, value: string | number) => void;
    onFieldInit?: (rowId: string, columnId: keyof TData, value: string) => void;
    getField?: (rowId: string, columnId: keyof TData) => Field | undefined;
    getHeaderComponent?: (column?: DynamicField) => ReactNode;
    resetFiltersDependencies?: Array<unknown>;
    onLastFieldTab?: (rowIndex: number, firstColumnId: keyof TData) => void;
    onCellClick?: (row: Row<TData>, columnId: keyof TData) => void;
    getRowId: (originalRow: TData, index: number, parent?: Row<TData>) => string;
    externalColumnFilters?: ColumnFiltersState;
    activeRow?: string;
    setActiveRow?: (activeRow: string | undefined, index: number) => void;
    placeholderRowDisabled?: boolean;
    onPlaceholderRowClick?: () => void;
    noRowsMessage?: ReactNode;
    onCurrentPageChange?: (rows: Array<Row<TData>>) => void;
}

export const COLUMN_ID_SELECT = 'select';
export const COLUMN_ID_QUICKMENU = 'quickMenu';
export const COLUMN_ID_PINNED = 'pinned';
export const NATIVE_COLUMNS = [COLUMN_ID_SELECT, COLUMN_ID_QUICKMENU, COLUMN_ID_PINNED];

export interface DataGridRowProps {
    wf_highlightcolor?: string;
}

const DataGrid = <TData extends DataGridRowProps>({
    columns,
    data,
    name,
    className,
    pageSize,
    getRowProps,
    storageKey,
    disableSettings,
    selectedRow,
    selectedCell,
    onCellSelect,
    onOrderChange,
    rowSelection,
    setRowSelection,
    enableRowSelection,
    renderNrOfRows,
    onFieldUpdated,
    onFieldInit,
    getField,
    getHeaderComponent,
    resetFiltersDependencies,
    onLastFieldTab,
    onCellClick,
    getRowId,
    activeRow,
    setActiveRow,
    externalColumnFilters,
    showTotals = false,
    placeholderRowDisabled,
    onPlaceholderRowClick,
    noRowsMessage,
    onCurrentPageChange,
}: Props<TData>) => {
    const { t } = useTranslation();
    const { openModal } = useModals();
    const {
        sorting,
        setSorting,
        resetSorting,
        columnSizing,
        setColumnSizing,
        columnOrder,
        setColumnOrder,
        columnVisibility,
        setColumnVisibility,
        columnFilters,
        setColumnFilters,
        resetColumnFilters,
        showFilters,
        setShowFilters,
    } = useDataGridState(storageKey);
    useEffect(() => {
        if (!externalColumnFilters) {
            return;
        }

        setColumnFilters(externalColumnFilters);
    }, [externalColumnFilters]);

    const handleOrderChange = () => {
        if (!onOrderChange) {
            return;
        }
        onOrderChange({
            sorting,
            columnFilters,
        });
    };

    const allColumns: Array<ColumnDef<TData>> = useMemo(() => {
        const columnHelper = createColumnHelper<TData>();

        if (!columns.length) {
            return columns;
        }

        return [
            columnHelper.display({
                id: COLUMN_ID_SELECT,
                size: 35,
                meta: {
                    className: 'dataGrid__cell dataGrid__cell--select',
                },
                enableResizing: false,
                header: (info: HeaderContext<TData, unknown>) => (
                    <>
                        {rowSelection ? (
                            <IndeterminateCheckbox
                                className="dataGrid__head-checkbox"
                                {...{
                                    checked: info.table.getIsAllPageRowsSelected(),
                                    indeterminate: info.table.getIsSomePageRowsSelected(),
                                    onChange: info.table.getToggleAllPageRowsSelectedHandler(),
                                    id: `${name ? `${name}_` : ''}${info.header.id}_all`,
                                }}
                            />
                        ) : (
                            <div className="dataGrid__head-checkbox-placeholder" />
                        )}
                    </>
                ),
                cell: (info: CellContext<TData, unknown>) => (
                    <>
                        {rowSelection ? (
                            <IndeterminateCheckbox
                                {...{
                                    checked: info.row.getIsSelected(),
                                    indeterminate: info.row.getIsSomeSelected(),
                                    onChange: info.row.getToggleSelectedHandler(),
                                    disabled: !info.row.getCanSelect(),
                                    id: `${name ? `${name}_` : ''}${info.cell.id}`,
                                }}
                            />
                        ) : (
                            <div className="dataGrid__head-checkbox-placeholder" />
                        )}
                    </>
                ),
            }),
            ...columns,
        ];
    }, [columns, rowSelection]);

    const pinnedColumn = allColumns.find((col) => col.id === COLUMN_ID_PINNED);
    const purifiedSorting = purifySorting<TData>(sorting, allColumns);
    const purifiedColumnFilters = purifyColumnFilters<TData>(columnFilters, allColumns);
    const purifiedColumnOrder = purifyColumnOrder<TData>(columnOrder, allColumns);

    const table = useReactTable({
        data,
        columns: allColumns,
        filterFns: {
            fuzzy: fuzzyFilter<TData>,
            numeric: numericFilter<TData>,
            dateRange: dateRangeFilter<TData>,
            select: selectFilter<TData>,
        },
        sortingFns: {
            numeric: numericSorting<TData>,
        },
        state: {
            rowSelection,
            columnSizing,
            columnOrder: purifiedColumnOrder,
            columnVisibility,
            columnFilters: purifiedColumnFilters,
            sorting: !pinnedColumn ? purifiedSorting : [{ id: COLUMN_ID_PINNED, desc: true }, ...purifiedSorting],
        },
        autoResetPageIndex: false,
        columnResizeMode: 'onChange',
        onSortingChange: setSorting,
        onColumnSizingChange: setColumnSizing,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        enableRowSelection,
        enableMultiSort: true,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getRowId,
        debugAll: false,
        initialState: {
            pagination: {
                pageSize: pageSize || 100,
            },
            rowSelection,
            columnSizing,
            columnOrder: purifiedColumnOrder,
            columnVisibility,
            columnFilters: purifiedColumnFilters,
            sorting: purifiedSorting,
        },
        meta: {
            ...(onFieldUpdated ? { onFieldUpdated } : {}),
            ...(onLastFieldTab ? { onLastFieldTab } : {}),
            ...(onFieldInit ? { onFieldInit } : {}),
            ...(selectedRow !== undefined ? { selectedRow } : {}),
            ...(selectedCell !== undefined ? { selectedCell } : {}),
            ...(onCellSelect ? { onCellSelect } : {}),
            ...(getField ? { getField } : {}),
            ...(name ? { name } : {}),
        },
    });

    useHotkeys(
        ARROW_DOWN,
        (keyboardEvent) => {
            if (!setActiveRow) {
                return;
            }

            const rows = table.getPaginationRowModel().rows;
            if (!rows.length) {
                return;
            }

            keyboardEvent.preventDefault();

            if (!activeRow) {
                setActiveRow(rows[0].id, 0);
                return;
            }

            const currentRowIndex = rows.findIndex((row) => row.id === activeRow);
            const nextRowIndex = currentRowIndex === rows.length - 1 ? 0 : currentRowIndex + 1;
            const nextRowId = nextRowIndex === rows.length ? rows[0].id : nextRowIndex < rows.length ? rows[nextRowIndex].id : undefined;
            setActiveRow(nextRowId, nextRowIndex);
        },
        [activeRow, setActiveRow],
    );

    useHotkeys(
        ARROW_UP,
        (keyboardEvent) => {
            if (!setActiveRow) {
                return;
            }

            const rows = table.getPaginationRowModel().rows;
            if (!rows.length) {
                return;
            }

            keyboardEvent.preventDefault();

            if (!activeRow) {
                setActiveRow(rows[rows.length - 1].id, rows.length - 1);
                return;
            }

            const currentRowIndex = rows.findIndex((row) => row.id === activeRow);
            const prevRowIndex = currentRowIndex === 0 ? rows.length - 1 : currentRowIndex - 1;
            const prevRowId = prevRowIndex === -1 ? rows[rows.length - 1].id : rows[prevRowIndex].id;
            setActiveRow(prevRowId, prevRowIndex);
        },
        [activeRow, setActiveRow],
    );

    useEffect(() => {
        if (!setRowSelection) {
            return;
        }
        // Reset row selection on grid change.
        table.resetRowSelection();
    }, [storageKey]);

    useEffect(() => {
        if (!resetFiltersDependencies?.length) {
            return;
        }

        resetColumnFilters();
    }, [resetFiltersDependencies]);

    const currentPage = table.getState().pagination.pageIndex;
    const visibleRows = table.getRowModel().rows;

    useEffect(() => {
        if (!onCurrentPageChange) {
            return;
        }

        onCurrentPageChange(visibleRows);
    }, [onCurrentPageChange, currentPage, visibleRows]);

    useEffect(() => {
        handleOrderChange();

        // Go to the first page when sorting changes.
        const currentPageIndex = table.getState().pagination.pageIndex;
        if (currentPageIndex === 0) {
            return;
        }
        table.setPageIndex(0);
    }, [sorting]);

    useEffect(() => {
        handleOrderChange();

        // Go to the first page when filters changes.
        const currentPageIndex = table.getState().pagination.pageIndex;
        if (currentPageIndex === 0) {
            return;
        }
        table.setPageIndex(0);
    }, [columnFilters]);

    // Go to the previous page when landing on empty page
    useEffect(() => {
        if (visibleRows.length || currentPage === 0) {
            return;
        }
        table.setPageIndex(currentPage - 1);
    }, [visibleRows, currentPage]);

    const paginationActivated = table.getPageCount() > 1;

    const onSettingsSaved = (columns: Array<ColumnSetting>) => {
        const hiddenColumns: VisibilityState = {};
        columns
            .filter((column) => !column.visible)
            .forEach((column) => {
                hiddenColumns[column.id] = false;
            });
        setColumnVisibility(hiddenColumns);

        setColumnOrder([COLUMN_ID_SELECT, ...columns.map((column) => column.id), COLUMN_ID_QUICKMENU]);
    };

    const onOpenGridSettings = () => {
        openModal({
            type: ModalType.GridSettings,
            size: Sizes.md,
            title: t('settingsModal.title', { ns: 'dataGrid' }),
            modalData: {
                columns: table
                    .getAllLeafColumns()
                    .filter((column) => column.accessorFn && !NATIVE_COLUMNS.includes(column.id))
                    .map((column) => {
                        return {
                            id: column.id,
                            visible: column.getIsVisible(),
                            label: column.columnDef.header as string,
                        };
                    }),
                originalOrder: columns
                    .filter((column) => {
                        return column.id && !NATIVE_COLUMNS.includes(column.id);
                    })
                    .map((column) => column.id as string),
                onSettingsSaved,
            },
        });
    };

    const someColumnsHaveFiltersEnabled = columns.some((column) => {
        if (typeof column.enableColumnFilter === 'boolean') {
            return column.enableColumnFilter;
        }

        return !!column.filterFn;
    });

    const extendedHead =
        (someColumnsHaveFiltersEnabled && showFilters) || (rowSelection !== undefined && className?.indexOf('dataGrid--noFilters-selectableRows') === -1 && showFilters);

    return (
        <>
            <div
                className={classNames('dataGrid', className, {
                    'dataGrid--pagination': paginationActivated,
                    'dataGrid--hasSettingsCog': !!storageKey && !disableSettings,
                    'dataGrid--hideFilters': !showFilters,
                    'dataGrid--extendedHead': extendedHead,
                })}
                style={{
                    width: table.getTotalSize(),
                }}
            >
                <DataGridHead<TData>
                    columns={columns}
                    table={table}
                    storageKey={storageKey}
                    getHeaderComponent={getHeaderComponent}
                    resetSorting={resetSorting}
                    resetColumnFilters={resetColumnFilters}
                    disableSettings={disableSettings}
                    purifiedColumnFilters={purifiedColumnFilters}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                    sorting={sorting}
                    onOpenGridSettings={onOpenGridSettings}
                    name={name}
                />
                <DataGridBody<TData>
                    table={table}
                    selectedRow={setActiveRow ? activeRow : selectedRow}
                    setActiveRow={setActiveRow}
                    selectedCell={selectedCell}
                    getRowProps={getRowProps}
                    onCellClick={onCellClick}
                    placeholderRowDisabled={placeholderRowDisabled}
                    onPlaceholderRowClick={onPlaceholderRowClick}
                    noRowsMessage={noRowsMessage}
                />
                <DataGridFooter<TData>
                    selectedRow={selectedRow}
                    data={data}
                    allColumns={allColumns}
                    table={table}
                    purifiedColumnFilters={purifiedColumnFilters}
                    purifiedSorting={purifiedSorting}
                    resetColumnFilters={resetColumnFilters}
                    resetSorting={resetSorting}
                    renderNrOfRows={renderNrOfRows}
                    showTotals={showTotals}
                />
            </div>
        </>
    );
};

export default DataGrid;
